// services/auth/authService.ts
import { backendClient } from '@/lib/backendClient';
import type { User, Session, AuthResponse, SessionResponse } from '@/types/auth';

export interface AuthProvider {
  signIn(email: string, password: string): Promise<AuthResponse>;
  signUp(email: string, password: string, metadata?: any): Promise<AuthResponse>;
  signOut(): Promise<{ error: any }>;
  signInWithGoogle(redirectTo: string): Promise<{ error: any }>;
  resetPassword(email: string, redirectTo: string): Promise<{ error: any }>;
  updatePassword(newPassword: string): Promise<{ error: any }>;
  getSession(): Promise<SessionResponse>;
  deleteAccount(): Promise<{ error: any }>;
}

// Backend Auth Provider (now the only provider)
// Former SupabaseAuthProvider removed - all auth now goes through backend

// Backend Auth Provider (new)
class BackendAuthProvider implements AuthProvider {
  private backendUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    this.backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000/api/v1';
    this.loadTokens();
  }

  private saveTokens(accessToken: string, refreshToken?: string) {
    this.accessToken = accessToken;
    if (refreshToken) {
      this.refreshToken = refreshToken;
    }
    localStorage.setItem('auth_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
  }

  private loadTokens() {
    this.accessToken = localStorage.getItem('auth_token');
    this.refreshToken = localStorage.getItem('refresh_token');
  }

  private clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
  }

  async signIn(email: string, password: string): Promise<AuthResponse> {
    try {
      // OAuth2PasswordRequestForm expects form data
      const formData = new FormData();
      formData.append('username', email); // OAuth2 spec uses 'username' for email
      formData.append('password', password);

      const response = await fetch(`${this.backendUrl}/auth/login`, {
        method: 'POST',
        body: formData,
        credentials: 'include', // Include cookies in request
      });

      if (!response.ok) {
        const error = await response.json();
        return { error: { message: error.detail || 'Login failed' } };
      }

      const data = await response.json();

      // Backend now sets httpOnly cookies, no tokens in response
      // We just get a success message

      // Fetch user profile using cookie authentication
      const userResponse = await fetch(`${this.backendUrl}/auth/me`, {
        credentials: 'include' // Include cookies
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        localStorage.setItem('user_data', JSON.stringify(userData));

        // Store a flag that user is authenticated
        localStorage.setItem('is_authenticated', 'true');

        // Store token expiry time (30 minutes from now)
        const expiryTime = Date.now() + 30 * 60 * 1000;
        localStorage.setItem('token_expiry', expiryTime.toString());

        return {
          data: {
            user: userData,
            session: { access_token: 'cookie-based' } // Placeholder for compatibility
          }
        };
      }

      return {
        data: {
          session: { access_token: 'cookie-based' }
        }
      };
    } catch (error: any) {
      return { error: { message: error.message || 'Network error' } };
    }
  }

  async signUp(_email: string, _password: string, _metadata?: any): Promise<AuthResponse> {
    // DEPRECATED: Direct signup is no longer supported.
    // Use backendClient.auth.initiateSignUp() and verifySignUpOTP() instead.
    // This ensures email verification via OTP before account creation.
    return {
      error: {
        message: 'Direct signup is no longer supported. Please use the OTP verification flow through the signup page.'
      }
    };
  }

  async signOut(): Promise<{ error: any }> {
    try {
      // Call logout endpoint to clear cookies
      await fetch(`${this.backendUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout request failed:', error);
    }

    // Clear local storage
    this.clearTokens();
    localStorage.removeItem('is_authenticated');
    localStorage.removeItem('token_expiry');
    return { error: null };
  }

    async signInWithGoogle(redirectTo: string): Promise<{ error: any }> {
    try {
        // Only pass state, let backend handle redirect_uri
        const params = new URLSearchParams({
        state: redirectTo || window.location.pathname
        });
        
        window.location.href = `${this.backendUrl}/auth/google?${params}`;
        return { error: null };
    } catch (error: any) {
        return { error: { message: error.message } };
    }
    }

  async resetPassword(email: string, redirectTo: string): Promise<{ error: any }> {
    try {
      const response = await fetch(`${this.backendUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, redirect_url: redirectTo }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { error: { message: error.detail || 'Password reset failed' } };
      }

      return { error: null };
    } catch (error: any) {
      return { error: { message: error.message || 'Network error' } };
    }
  }

  async updatePassword(newPassword: string): Promise<{ error: any }> {
    try {
      const response = await fetch(`${this.backendUrl}/auth/reset-password`, {
        method: 'POST',
        credentials: 'include', // Use cookie-based authentication
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_password: newPassword }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { error: { message: error.detail || 'Password update failed' } };
      }

      return { error: null };
    } catch (error: any) {
      return { error: { message: error.message || 'Network error' } };
    }
  }

  async getSession(): Promise<SessionResponse> {
    if (this.accessToken) {
      const userData = localStorage.getItem('user_data');
      return {
        data: {
          session: {
            access_token: this.accessToken,
            user: userData ? JSON.parse(userData) : null
          }
        }
      };
    }
    return { data: { session: null } };
  }

  async deleteAccount(): Promise<{ error: any }> {
    try {
      const response = await fetch(`${this.backendUrl}/auth/delete-account`, {
        method: 'DELETE',
        credentials: 'include' // Use cookie-based authentication
      });

      if (!response.ok) {
        const error = await response.json();
        return { error: { message: error.detail || 'Account deletion failed' } };
      }

      this.clearTokens();
      return { error: null };
    } catch (error: any) {
      return { error: { message: error.message || 'Network error' } };
    }
  }
}

// Auth Service - now only uses backend
class AuthService {
  private provider: AuthProvider;

  constructor() {
    // Always use backend auth provider
    this.provider = new BackendAuthProvider();
  }

  get auth() {
    return this.provider;
  }
}

export const authService = new AuthService();